// Minimal stub of the React Native API for the marketing site build.
// This prevents Vite from trying to parse the real `react-native` package,
// which uses Flow/JSX syntax that isn't compatible with our tooling.

export const View = () => null;
export const Text = () => null;
export const ScrollView = () => null;
export const Image = () => null;
export const Pressable = () => null;
export const TouchableOpacity = () => null;
export const FlatList = () => null;

// Commonly-imported helpers/components that some libraries expect.
export const AppRegistry = {
  registerComponent(_appKey: string, _component: () => any) {
    // no-op on web marketing site
  },
  registerRunnable(_appKey: string, _runnable: any) {
    // no-op
  },
  runApplication(_appKey: string, _appParameters: any) {
    // no-op
  },
};

export const StyleSheet = {
  create<T extends Record<string, any>>(styles: T): T {
    return styles;
  },
};

export const Platform = {
  OS: 'web',
  select<T>(options: { web?: T; default?: T } & Record<string, T | undefined>): T | undefined {
    return options.web ?? options.default;
  },
};

// Dummy TurboModuleRegistry for libraries like expo-modules-core that
// import it from 'react-native'. On the marketing site we never actually
// use native modules, so this can safely be a no-op.
export const TurboModuleRegistry = {
  get<T = any>(_name: string): T | null {
    return null;
  },
};

// Minimal no-op event emitter compatible with React Native's NativeEventEmitter shape.
export class NativeEventEmitter {
  addListener(_eventType: string, _listener: (...args: any[]) => void, _context?: any) {
    return {
      remove() {
        // no-op
      },
    };
  }

  removeAllListeners(_eventType?: string) {
    // no-op
  }

  removeSubscription(_subscription: { remove: () => void }) {
    // no-op
  }
}

// NativeModules is never used on the marketing site, so keep it empty.
export const NativeModules: Record<string, any> = {};

// Some libraries reference DeviceEventEmitter directly.
export const DeviceEventEmitter = new NativeEventEmitter();

// Simple Linking stub: never actually opens anything, but preserves API shape.
export const Linking = {
  async openURL(_url: string): Promise<boolean> {
    return false;
  },
  async canOpenURL(_url: string): Promise<boolean> {
    return false;
  },
  addEventListener(
    _type: string,
    _handler: (...args: any[]) => void,
  ) {
    return {
      remove() {
        // no-op
      },
    };
  },
  removeAllListeners(_type: string) {
    // no-op
  },
};

// Basic Dimensions stub.
export const Dimensions = {
  get(_dim: 'window' | 'screen') {
    return {
      width: 0,
      height: 0,
      scale: 1,
      fontScale: 1,
    };
  },
  addEventListener(
    _type: 'change',
    _handler: (...args: any[]) => void,
  ) {
    return {
      remove() {
        // no-op
      },
    };
  },
  removeEventListener(_type: 'change', _handler: (...args: any[]) => void) {
    // no-op
  },
};

// PixelRatio stub.
export const PixelRatio = {
  get() {
    return 1;
  },
  getFontScale() {
    return 1;
  },
};

// Appearance + hook commonly used for theming.
export const Appearance = {
  getColorScheme(): 'light' | 'dark' | null {
    return 'light';
  },
  addChangeListener(_listener: (prefs: { colorScheme: 'light' | 'dark' | null }) => void) {
    return {
      remove() {
        // no-op
      },
    };
  },
  removeChangeListener(
    _listener: (prefs: { colorScheme: 'light' | 'dark' | null }) => void,
  ) {
    // no-op
  },
};

export function useColorScheme(): 'light' | 'dark' | null {
  return 'light';
}

// requireNativeComponent stub returns a no-op component.
export function requireNativeComponent<T = any>(_viewName: string): (props: T) => null {
  return () => null;
}

export default {
  View,
  Text,
  ScrollView,
  Image,
  Pressable,
  TouchableOpacity,
  FlatList,
  StyleSheet,
  Platform,
  TurboModuleRegistry,
  NativeEventEmitter,
  NativeModules,
  DeviceEventEmitter,
  Linking,
  Dimensions,
  PixelRatio,
  Appearance,
  useColorScheme,
  requireNativeComponent,
  AppRegistry,
};

